import React from 'react';
import { combinedLegForArrow, type PaymentMethod } from './paymentMeta';
import type { CombinedLeg } from './usePaymentFlow';

export type UseCombinedLegKeysOptions = {
    method: PaymentMethod;
    activeLeg: CombinedLeg;
};

export type CombinedLegKeys = {
    /** Each leg's input: `↑`/`↓` move real focus there, and the field's own `onFocus` then selects the leg. */
    legInputRefs: Record<CombinedLeg, React.RefObject<HTMLInputElement | null>>;
    /** For the method group, on the capture phase — see `useCombinedLegKeys`. */
    onMethodGroupKeyDown: (event: React.KeyboardEvent) => void;
    /** For the two legs' wrapper: `↑`/`↓` step between the legs, no wraparound. */
    onLegsKeyDown: (event: React.KeyboardEvent) => void;
};

/**
 * `↑`/`↓` across a split payment (bos-seller#59). Inside the legs they step
 * between the cash and card fields. On the method group, while the split is
 * selected, `↓` enters the cash leg and `↑` the card leg instead of changing
 * the method — the group is a react-aria `RadioGroup`, whose own arrow
 * handling would select another method and wipe the split, so this reads the
 * key on the capture phase and stops it there. `←`/`→` pass through and
 * still switch the method.
 *
 * Focus moves for real, to the leg's input; its `onFocus` makes it the
 * active leg, so there is no second "selected leg" state to keep in sync.
 * Which leg a key lands on is the pure `combinedLegForArrow`.
 */
export const useCombinedLegKeys = ({
    method,
    activeLeg,
}: UseCombinedLegKeysOptions): CombinedLegKeys => {
    const cashInputRef = React.useRef<HTMLInputElement>(null);
    const cardInputRef = React.useRef<HTMLInputElement>(null);
    const legInputRefs = React.useMemo(
        () => ({ cash: cashInputRef, card: cardInputRef }),
        [],
    );

    const onMethodGroupKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            if (method !== 'combined') {
                return;
            }
            const leg = combinedLegForArrow(event.key, 'methods');
            if (!leg) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            legInputRefs[leg].current?.focus();
        },
        [method, legInputRefs],
    );

    const onLegsKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            const leg = combinedLegForArrow(event.key, activeLeg);
            if (!leg) {
                return;
            }
            event.preventDefault();
            legInputRefs[leg].current?.focus();
        },
        [activeLeg, legInputRefs],
    );

    return { legInputRefs, onMethodGroupKeyDown, onLegsKeyDown };
};
